import type { RouteObject } from 'react-router';
import { AppShell } from '../components/AppShell';
import { AuthLayout } from '../components/AuthLayout';
import { RequireAnonymous, RequireRole } from '../auth/RequireRole';
import { SectionTabs } from '../components/SectionTabs';

import { Login } from './auth/Login';
import { Register } from './auth/Register';
import { ForgotPassword } from './auth/ForgotPassword';
import { ResetPassword } from './auth/ResetPassword';
import { VerifyEmail } from './auth/VerifyEmail';

import { Dashboard } from './client/Dashboard';
import { Training } from './client/Training';
import { WorkoutSession } from './client/WorkoutSession';
import { Nutrition } from './client/Nutrition';
import { Progress } from './client/Progress';
import { WeeklySummary } from './client/WeeklySummary';
import { Education } from './client/Education';
import { EducationArticle } from './client/EducationArticle';
import { Messages } from './client/Messages';
import { CheckIn } from './client/CheckIn';
import { Habits } from './client/Habits';
import { Calendar } from './client/Calendar';
import { Profile } from './client/Profile';
import { Subscription } from './client/Subscription';
import { Onboarding } from './onboarding/Onboarding';

import { TrainerDashboard } from './trainer/TrainerDashboard';
import { TrainerClients } from './trainer/TrainerClients';
import { TrainerClientDetail } from './trainer/TrainerClientDetail';
import { TrainerPrograms } from './trainer/TrainerPrograms';
import { ProgramBuilder } from './trainer/ProgramBuilder';
import { TrainerExercises } from './trainer/TrainerExercises';
import { TrainerMealTemplates } from './trainer/TrainerMealTemplates';
import { TrainerTransformations } from './trainer/TrainerTransformations';
import { TrainerMeasurementGuide } from './trainer/TrainerMeasurementGuide';
import { TrainerMessages } from './trainer/TrainerMessages';
import { TrainerCheckIns } from './trainer/TrainerCheckIns';

import { AdminDashboard } from './admin/AdminDashboard';
import { AdminUsers } from './admin/AdminUsers';
import { AdminProducts } from './admin/AdminProducts';
import { AdminContent } from './admin/AdminContent';
import { AdminPayments } from './admin/AdminPayments';
import { AdminSubmissions } from './admin/AdminSubmissions';
import { AdminAnalytics } from './admin/AdminAnalytics';
import { AdminDiscountCodes } from './admin/AdminDiscountCodes';

import { NotFound } from './NotFound';

/**
 * URL je izvor istine za stanje. Nema toggle klasa na tabovima, nema drugog routera
 * (ARHITEKTURA.md sekcija 1). Svaka privatna grana ima guard po ulozi.
 */
export const routes: RouteObject[] = [
  {
    element: (
      <RequireAnonymous>
        <AuthLayout />
      </RequireAnonymous>
    ),
    children: [
      { path: '/prijava', element: <Login /> },
      { path: '/registracija', element: <Register /> },
      { path: '/zaboravljena-lozinka', element: <ForgotPassword /> },
      { path: '/reset', element: <ResetPassword /> },
      { path: '/potvrda-emaila', element: <VerifyEmail /> },
    ],
  },
  {
    path: '/onboarding',
    element: (
      <RequireRole roles={['client']}>
        <Onboarding />
      </RequireRole>
    ),
  },
  {
    element: (
      <RequireRole roles={['client']}>
        <AppShell />
      </RequireRole>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: '/trening', element: <Training /> },
      { path: '/trening/:id', element: <WorkoutSession /> },
      { path: '/prehrana', element: <Nutrition /> },
      { path: '/napredak', element: <Progress /> },
      // Obavijest o tjednom sažetku vodi na /napredak; /sazetak je isti sadržaj sam za sebe.
      { path: '/sazetak', element: <WeeklySummary /> },
      { path: '/edukacija', element: <Education /> },
      { path: '/edukacija/:slug', element: <EducationArticle /> },
      { path: '/navike', element: <Habits /> },
      { path: '/check-in', element: <CheckIn /> },
      // Razgovor je vlastita ruta: na mobitelu je popis zaseban prikaz.
      { path: '/poruke', element: <Messages /> },
      { path: '/poruke/:conversationId', element: <Messages /> },
      { path: '/pretplata', element: <Subscription /> },
    ],
  },
  {
    element: (
      <RequireRole roles={['trainer', 'admin']}>
        <AppShell />
      </RequireRole>
    ),
    children: [
      {
        element: (
          <SectionTabs
            items={[
              { to: '/trener', labelKey: 'portal.dashboard', end: true },
              { to: '/trener/klijenti', labelKey: 'shell.clients' },
              { to: '/trener/programi', labelKey: 'shell.programs' },
              { to: '/trener/vjezbe', labelKey: 'shell.exercises' },
              { to: '/trener/obroci', labelKey: 'mealTemplates.title' },
              { to: '/trener/mjerenje', labelKey: 'measureGuide.title' },
          { to: '/trener/transformacije', labelKey: 'transformacije.title' },
              { to: '/trener/poruke', labelKey: 'trainer.messages' },
              { to: '/trener/check-in', labelKey: 'trainer.checkIns' },
            ]}
          />
        ),
        children: [
          { path: '/trener', element: <TrainerDashboard /> },
          { path: '/trener/poruke', element: <TrainerMessages /> },
          { path: '/trener/poruke/:conversationId', element: <TrainerMessages /> },
          { path: '/trener/check-in', element: <TrainerCheckIns /> },
          { path: '/trener/klijenti', element: <TrainerClients /> },
          { path: '/trener/klijenti/:id', element: <TrainerClientDetail /> },
          /* Odjeljci kartona su URL, ne stanje u komponenti — sekcija 1. */
          { path: '/trener/klijenti/:id/:sekcija', element: <TrainerClientDetail /> },
          { path: '/trener/mjerenje', element: <TrainerMeasurementGuide /> },
          { path: '/trener/transformacije', element: <TrainerTransformations /> },
          { path: '/trener/programi', element: <TrainerPrograms /> },
          { path: '/trener/programi/:id', element: <ProgramBuilder /> },
          { path: '/trener/vjezbe', element: <TrainerExercises /> },
          { path: '/trener/obroci', element: <TrainerMealTemplates /> },
        ],
      },
    ],
  },
  {
    element: (
      <RequireRole roles={['admin']}>
        <AppShell />
      </RequireRole>
    ),
    children: [
      {
        element: (
          <SectionTabs
            items={[
              { to: '/admin', labelKey: 'portal.dashboard', end: true },
              { to: '/admin/korisnici', labelKey: 'shell.users' },
              { to: '/admin/proizvodi', labelKey: 'shell.products' },
              { to: '/admin/placanja', labelKey: 'shell.payments' },
              { to: '/admin/upiti', labelKey: 'shell.submissions' },
              { to: '/admin/sadrzaj', labelKey: 'shell.content' },
              { to: '/admin/kodovi', labelKey: 'shell.discountCodes' },
              { to: '/admin/analitika', labelKey: 'shell.analytics' },
            ]}
          />
        ),
        children: [
          { path: '/admin', element: <AdminDashboard /> },
          { path: '/admin/korisnici', element: <AdminUsers /> },
          { path: '/admin/proizvodi', element: <AdminProducts /> },
          { path: '/admin/sadrzaj', element: <AdminContent /> },
          { path: '/admin/placanja', element: <AdminPayments /> },
          { path: '/admin/upiti', element: <AdminSubmissions /> },
          { path: '/admin/kodovi', element: <AdminDiscountCodes /> },
          { path: '/admin/analitika', element: <AdminAnalytics /> },
        ],
      },
    ],
  },
  {
    // Profil je zajednički svim ulogama.
    element: (
      <RequireRole roles={['client', 'trainer', 'admin']}>
        <AppShell />
      </RequireRole>
    ),
    children: [
      { path: '/profil', element: <Profile /> },
      // Kalendar je zajednički: klijent gleda svoj, trener bira klijenta i zakazuje termine.
      { path: '/kalendar', element: <Calendar /> },
    ],
  },
  { path: '*', element: <NotFound /> },
];
